
import { Vue, Component, Prop } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { SUPPORTED_LOCALES } from '@/utils/constants';
import getBrowserLocale, { resolveLocale, getSavedLocale } from '@/utils/i18n-helpers';
const clickaway = require('vue-clickaway').mixin;

const namespace: string = 'auth';

@Component<LocaleSwitcher>({
	mixins: [ clickaway ]
})

export default class LocaleSwitcher extends Vue {
	@Action('setLocale', { namespace }) private setLocale!: (locale: string) => void;
	@Getter('getRestaurant', { namespace: 'restaurant' }) private restaurant!: Restaurant;
	@Prop({ type: Boolean, required: false, default: false }) private abbrLocale!: boolean;
	@Prop({ type: String, required: false, default: 'left' }) private align!: 'left' | 'right';

	private supportedLocales: { value: string, label: string }[] = SUPPORTED_LOCALES;
	private opened: boolean = false;

	private get dropdownLabel(): string | undefined {
		if (this.abbrLocale) {
			return resolveLocale(this.$i18n.locale, this.restaurant?.locales).toUpperCase();
		}
		else if (!this.abbrLocale && this.supportedLocales) {
			return this.supportedLocales.find(locale => locale.value === this.$i18n.locale)?.label;
		}
	}

	/**
	 * List the supported locales the restaurant offers (intersection with the global list),
	 * then move the browser locale to the top of the list when it matches one of them
	 *
	 * @return {Array<{ value: string, label: string }>}
	 */
	private get localesList(): { value: string, label: string }[] {
		const restaurantLocaleShorts = this.restaurant?.locales?.map((locale: RestaurantLocale) => locale.locale_short.toLowerCase());
		const tempLocales = restaurantLocaleShorts?.length
			? this.supportedLocales.filter(supportedLocale => restaurantLocaleShorts.includes(resolveLocale(supportedLocale.value).toLowerCase()))
			: [...this.supportedLocales];
		const localeIndex = tempLocales.findIndex(locale => locale.value === getBrowserLocale());

		if (localeIndex > 0) {
			const locale = tempLocales.splice(localeIndex, 1)[0];
        	tempLocales.unshift(locale);
		}
		return tempLocales;
	}

	/**
	 * Check if a supported locale matches the currently active locale,
	 * comparing codes resolved against the restaurant's locales so en-CA and
	 * en-GB don't collide, and so a saved locale the restaurant doesn't offer
	 * (e.g. 'en-GB' on a Canadian restaurant) still highlights its family
	 *
	 * @param {string} value
	 * @return {boolean}
	 */
	private isActiveLocale(value: string): boolean {
		return resolveLocale(value, this.restaurant?.locales) === resolveLocale(this.$i18n.locale, this.restaurant?.locales);
	}

	/**
	 * Close the dropdown on clickaway
	 *
	 * @return {void}
	 */
	private away(): void {
		if(this.opened) {
			this.opened = false;
		}
	}

	/**
	 * Select the correct locale and force reload for the changes to be applied in
	 * the vuex store and utils. We save the locale in the cookies/localstorage to use it on reload
	 * If the locale selected is the same as the current one, we don't reload the page
	 *
	 * @param {string} value
	 * @return {void}
	 */
	private selectLocale(locale: string): void {
		const currentLocale = getSavedLocale() || null;
		if (currentLocale !== locale) {
			this.setLocale(locale);
			window.location.reload();
		}
		this.opened = false;
	}
}
